/**
 * 10.2.1 KVKK Onay Checkboxları
 *
 * Kayıt formu öncesi gösterilmesi gereken KVKK ve gizlilik onay kutucukları.
 * Kullanım: Kayıt formlarına bu bileşeni ekleyin ve onConsent callback'ini kullanın.
 *
 * Props:
 *   consents: { kvkk: bool, terms: bool, acikRiza: bool, marketing: bool }
 *   onChange: (key, value) => void
 *   errors: string[] (doğrulama hataları)
 */
import React from 'react';
import { Link } from 'react-router-dom';

const CONSENT_ITEMS = [
    {
        key: 'kvkk',
        required: true,
        label: (
            <>
                <Link to="/legal/kvkk-aydinlatma" target="_blank" className="text-[#D4AF37] hover:underline font-medium">
                    KVKK Aydınlatma Metni
                </Link>
                'ni okudum ve kişisel verilerimin işlenmesini kabul ediyorum.{' '}
                <span className="text-red-500 font-bold">*</span>
            </>
        ),
    },
    {
        key: 'terms',
        required: true,
        label: (
            <>
                <Link to="/legal/kullanim-kosullari" target="_blank" className="text-[#D4AF37] hover:underline font-medium">
                    Kullanım Koşulları
                </Link>
                'nı ve{' '}
                <Link to="/legal/gizlilik-politikasi" target="_blank" className="text-[#D4AF37] hover:underline font-medium">
                    Gizlilik Politikası
                </Link>
                'nı okudum, kabul ediyorum.{' '}
                <span className="text-red-500 font-bold">*</span>
            </>
        ),
    },
    {
        key: 'acikRiza',
        required: false,
        label: (
            <>
                <Link to="/legal/acik-riza" target="_blank" className="text-[#D4AF37] hover:underline font-medium">
                    Açık Rıza Metni
                </Link>
                'ni onaylıyorum — verilerimin ticari amaçlarla işlenmesine izin veriyorum. (İsteğe bağlı)
            </>
        ),
    },
    {
        key: 'marketing',
        required: false,
        label: 'Kampanya, indirim ve yeni ürün bildirimlerinden haberdar olmak istiyorum. (İsteğe bağlı)',
    },
];

const KVKKCheckboxes = ({ consents, onChange, errors = [] }) => {
    return (
        <div className="space-y-3 mt-4">
            <p className="text-xs text-gray-500 mb-2">
                Üyelik oluşturmak için aşağıdaki zorunlu metinleri onaylamanız gerekmektedir:
            </p>

            {CONSENT_ITEMS.map(({ key, required, label }) => (
                <label
                    key={key}
                    className={`flex items-start gap-3 cursor-pointer p-3 rounded-lg border transition-all ${consents[key]
                            ? 'border-[#D4AF37] bg-[#D4AF37]/5'
                            : required && errors.includes(key)
                                ? 'border-red-300 bg-red-50'
                                : 'border-gray-200 hover:border-[#D4AF37]/50'
                        }`}
                >
                    <div className="relative flex-shrink-0 mt-0.5">
                        <input
                            type="checkbox"
                            className="sr-only"
                            checked={consents[key] || false}
                            onChange={(e) => onChange(key, e.target.checked)}
                            required={required}
                        />
                        <div
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${consents[key]
                                    ? 'bg-[#D4AF37] border-[#D4AF37]'
                                    : 'bg-white border-gray-300'
                                }`}
                        >
                            {consents[key] && (
                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            )}
                        </div>
                    </div>
                    <span className="text-sm text-gray-700 leading-relaxed">{label}</span>
                </label>
            ))}

            {errors.length > 0 && (
                <p className="text-xs text-red-600 mt-1">
                    ⚠️ Zorunlu onay kutucuklarını işaretleyin: {errors.join(', ')}
                </p>
            )}
        </div>
    );
};

/**
 * Validation helper
 * Returns [] if all required consents are given, else returns array of missing required keys.
 */
export function validateKVKKConsents(consents) {
    const required = CONSENT_ITEMS.filter((i) => i.required).map((i) => i.key);
    return required.filter((key) => !consents[key]);
}

export default KVKKCheckboxes;
